/**
 * AP-48 — **Bautagebuch** (Baustein `SITE_DOCS.DIARY`): was Gerät, ERP und Server über den
 * Bautagesbericht gleich wissen müssen — Inhalt, Pflichtangaben, Vorbelegung und vor allem der
 * **kanonische Fachteil** (`tagebuchFachteil`), den beide Seiten mit derselben Funktion bauen
 * (AP-46 6.).
 *
 * Spezifikation: docs/Arbeitspakete/AP-48/AP-48_Bautagebuch.md
 */
import { festeZahl, kanonischerText } from './kanonisch'

/** Fassung des Fachteils. Eine Änderung am Aufbau unten ist eine neue Fassung (AP-46 6.). */
export const TAGEBUCH_SCHEMA_VERSION = 1

export const TAGEBUCH_WETTER_LAGEN = ['SONNIG', 'HEITER', 'BEWOELKT', 'NEBEL', 'REGEN', 'SCHNEE', 'GEWITTER', 'FROST', 'HITZE', 'STURM'] as const
export type TagebuchWetterLage = (typeof TAGEBUCH_WETTER_LAGEN)[number]

export const TAGEBUCH_NIEDERSCHLAG = ['KEINER', 'LEICHT', 'STARK'] as const
export type TagebuchNiederschlag = (typeof TAGEBUCH_NIEDERSCHLAG)[number]

export const TAGEBUCH_WIND = ['SCHWACH', 'MAESSIG', 'STARK', 'STURM'] as const
export type TagebuchWind = (typeof TAGEBUCH_WIND)[number]

/** Arten einer Behinderung (4.4) — im Inhalt deutsch, in der Datenbank `SiteHindranceKind`. */
export const TAGEBUCH_BEHINDERUNG_ARTEN = ['WITTERUNG', 'VORLEISTUNG', 'PLAENE', 'ANORDNUNG', 'ZUGANG', 'SONSTIGES'] as const
export type TagebuchBehinderungArt = (typeof TAGEBUCH_BEHINDERUNG_ARTEN)[number]

/** Inhalt ↔ `SiteHindranceKind` — eine Stelle für beide Richtungen. */
export const TAGEBUCH_BEHINDERUNG_DB = {
  WITTERUNG: 'WEATHER',
  VORLEISTUNG: 'PREDECESSOR',
  PLAENE: 'PLANS',
  ANORDNUNG: 'ORDER',
  ZUGANG: 'ACCESS',
  SONSTIGES: 'OTHER',
} as const satisfies Record<TagebuchBehinderungArt, string>
export type TagebuchBehinderungDb = (typeof TAGEBUCH_BEHINDERUNG_DB)[TagebuchBehinderungArt]

export function tagebuchArtAusDb(kind: string): TagebuchBehinderungArt {
  const treffer = (Object.entries(TAGEBUCH_BEHINDERUNG_DB) as [TagebuchBehinderungArt, string][]).find(([, db]) => db === kind)
  return treffer ? treffer[0] : 'SONSTIGES'
}

/** Qualifikation einer Buchung ohne Angabe (4.2). */
export const TAGEBUCH_OHNE_QUALIFIKATION = 'ohne Angabe'

/** Höchstens so viele Einträge je Liste — mehr schreibt niemand an einem Tag. */
export const TAGEBUCH_HOECHSTENS_ZEILEN = 100

// ── Inhalt, wie Gerät und ERP ihn führen ─────────────────────────────────────

/**
 * `vorbelegt` (4.3): aus Buchungen, Wareneingängen oder dem letzten Bericht übernommen und noch nicht
 * angefasst — Anzeige, kein Inhalt. Dasselbe gilt für `artikelId` (Zuordnung, AP-46 BD-24) und
 * `fortgeschrieben` an einer Behinderung.
 */
export interface TagebuchEigene {
  qualifikation: string
  anzahl: number | string
  stunden: number | string
  vorbelegt?: boolean
}

export interface TagebuchFremd {
  firma: string
  anzahl: number | string
  taetigkeit: string | null
  vorbelegt?: boolean
}

export interface TagebuchGeraet {
  bezeichnung: string
  artikelnummer: string | null
  anzahl: number | string
  stunden: number | string | null
  /** Zuordnung zum Artikelstamm (`EQUIPMENT`) — nicht im Inhalt. */
  artikelId?: string | null
  vorbelegt?: boolean
}

export interface TagebuchLieferung {
  text: string
  lieferant: string | null
  lieferschein: string | null
  vorbelegt?: boolean
}

export interface TagebuchBehinderung {
  nr: number
  /** UUID der ersten Erfassung — fortgeschrieben bleibt sie (4.4). */
  kette: string
  art: TagebuchBehinderungArt
  text: string
  seit: string
  bis: string | null
  auswirkung: string | null
  mehrkosten: boolean
  angezeigtAm: string | null
  /** aus dem letzten Bericht übernommen — die App fragt „dauert an?" */
  fortgeschrieben?: boolean
  /** Art aus dem Diktat — markiert, bis der Erfasser sie bestätigt (13.) */
  artVorschlag?: boolean
}

export interface TagebuchAnordnung {
  von: string
  text: string
}

export interface TagebuchUnterbrechung {
  von: string
  bis: string
  grund: string
}

export interface TagebuchWetter {
  lage: TagebuchWetterLage[]
  tempMorgen: string | number | null
  tempMittag: string | number | null
  niederschlag: TagebuchNiederschlag | null
  wind: TagebuchWind | null
  unterbrechung: TagebuchUnterbrechung | null
}

/** Der Fachinhalt eines Bautagesberichts, wie er übertragen wird (Kopf-`content` samt Behinderungen). */
export interface TagebuchInhalt {
  arbeitszeit: { beginn: string | null; ende: string | null }
  wetter: TagebuchWetter
  /** „heute keine Arbeiten" mit Grund — dann dürfen Belegschaft und Leistungen leer sein (TB-18). */
  stillstand: string | null
  belegschaft: { eigene: TagebuchEigene[]; namen: string[] | null; fremd: TagebuchFremd[] }
  geraete: TagebuchGeraet[]
  leistungen: string[]
  lieferungen: TagebuchLieferung[]
  behinderungen: TagebuchBehinderung[]
  anordnungen: TagebuchAnordnung[]
  vorkommnisse: string | null
  bemerkung: string | null
}

export function tagebuchLeer(): TagebuchInhalt {
  return {
    arbeitszeit: { beginn: null, ende: null },
    wetter: { lage: [], tempMorgen: null, tempMittag: null, niederschlag: null, wind: null, unterbrechung: null },
    stillstand: null,
    belegschaft: { eigene: [], namen: null, fremd: [] },
    geraete: [],
    leistungen: [],
    lieferungen: [],
    behinderungen: [],
    anordnungen: [],
    vorkommnisse: null,
    bemerkung: null,
  }
}

const liste = <T>(x: unknown): T[] => (Array.isArray(x) ? (x as T[]) : [])
const textOder = (x: unknown): string | null => (typeof x === 'string' ? x : x == null ? null : String(x))

/**
 * Ein beliebiger Inhalt in der Form oben — fehlende Teile leer. Prüft nicht (das tut der Server beim
 * Eintreffen), sondern macht nur lesbar, was eine ältere App-Fassung oder ein leerer Entwurf liefert.
 */
export function tagebuchInhalt(roh: unknown): TagebuchInhalt {
  const c = (roh && typeof roh === 'object' ? roh : {}) as Record<string, unknown>
  const leer = tagebuchLeer()
  const az = (c.arbeitszeit ?? {}) as Record<string, unknown>
  const w = (c.wetter ?? {}) as Record<string, unknown>
  const b = (c.belegschaft ?? {}) as Record<string, unknown>
  const u = w.unterbrechung as Record<string, unknown> | null | undefined
  return {
    arbeitszeit: { beginn: textOder(az.beginn), ende: textOder(az.ende) },
    wetter: {
      lage: liste<TagebuchWetterLage>(w.lage).filter((l) => (TAGEBUCH_WETTER_LAGEN as readonly string[]).includes(l)),
      tempMorgen: (w.tempMorgen as string | number | null | undefined) ?? null,
      tempMittag: (w.tempMittag as string | number | null | undefined) ?? null,
      niederschlag: (TAGEBUCH_NIEDERSCHLAG as readonly unknown[]).includes(w.niederschlag) ? (w.niederschlag as TagebuchNiederschlag) : null,
      wind: (TAGEBUCH_WIND as readonly unknown[]).includes(w.wind) ? (w.wind as TagebuchWind) : null,
      unterbrechung: u && typeof u === 'object' ? { von: textOder(u.von) ?? '', bis: textOder(u.bis) ?? '', grund: textOder(u.grund) ?? '' } : null,
    },
    stillstand: textOder(c.stillstand),
    belegschaft: {
      eigene: liste<TagebuchEigene>(b.eigene),
      namen: Array.isArray(b.namen) ? (b.namen as unknown[]).map((n) => String(n ?? '')) : null,
      fremd: liste<TagebuchFremd>(b.fremd),
    },
    geraete: liste<TagebuchGeraet>(c.geraete),
    leistungen: liste<unknown>(c.leistungen).map((l) => String(l ?? '')),
    lieferungen: liste<TagebuchLieferung>(c.lieferungen),
    behinderungen: liste<TagebuchBehinderung>(c.behinderungen),
    anordnungen: liste<TagebuchAnordnung>(c.anordnungen),
    vorkommnisse: textOder(c.vorkommnisse) ?? leer.vorkommnisse,
    bemerkung: textOder(c.bemerkung) ?? leer.bemerkung,
  }
}

// ── Zahlen ───────────────────────────────────────────────────────────────────

/** Eine Eingabe als Zahl — Komma oder Punkt; leer oder unlesbar → `null`. */
export function tagebuchZahl(wert: number | string | null | undefined): number | null {
  if (wert == null) return null
  if (typeof wert === 'number') return Number.isFinite(wert) ? wert : null
  const t = wert.trim().replace(',', '.')
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** `HH:mm` mit zwei Stellen — `7:5` gibt es nicht, `7:05` wird `07:05`; unlesbar → `null`. */
export function tagebuchUhrzeit(zeit: string | null | undefined): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((zeit ?? '').trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

const nichtLeer = (t: string | null | undefined): string | null => {
  const k = t == null ? '' : kanonischerText(t)
  return k ? k : null
}

const ganz = (wert: number | string | null | undefined): string => festeZahl(Math.round(tagebuchZahl(wert) ?? 0), 0)
const ganzOderNull = (wert: number | string | null | undefined): string | null => {
  const n = tagebuchZahl(wert)
  return n === null ? null : festeZahl(Math.round(n), 0)
}
const stunden = (wert: number | string | null | undefined): string => festeZahl(tagebuchZahl(wert) ?? 0, 2)

// ── Der kanonische Fachteil (5.1) ────────────────────────────────────────────

/**
 * **Der kanonische Fachteil**, `schemaVersion` 1 (AP-48 5.1). Zahlen als Text mit fester Stellenzahl:
 * Anzahl und Temperatur ganzzahlig, Stunden mit zwei Nachkommastellen. Listen in Erfassungsreihenfolge,
 * Behinderungen nach Nr. Zuordnungen (Artikel-ID) und Anzeigemarken (`vorbelegt`, `fortgeschrieben`)
 * stehen bewusst **nicht** darin (AP-46 BD-24); die Artikelnummer schon — als Schnappschuss.
 */
export function tagebuchFachteil(roh: TagebuchInhalt | Record<string, unknown> | null): Record<string, unknown> {
  const i = tagebuchInhalt(roh)
  const u = i.wetter.unterbrechung
  return {
    arbeitszeit: { beginn: tagebuchUhrzeit(i.arbeitszeit.beginn), ende: tagebuchUhrzeit(i.arbeitszeit.ende) },
    wetter: {
      // Mehrfachauswahl in der festen Reihenfolge der Liste — auf jedem Gerät gleich, gleich wie getippt.
      lage: TAGEBUCH_WETTER_LAGEN.filter((l) => i.wetter.lage.includes(l)),
      tempMorgen: ganzOderNull(i.wetter.tempMorgen),
      tempMittag: ganzOderNull(i.wetter.tempMittag),
      niederschlag: i.wetter.niederschlag,
      wind: i.wetter.wind,
      unterbrechung: u ? { von: tagebuchUhrzeit(u.von) ?? kanonischerText(u.von), bis: tagebuchUhrzeit(u.bis) ?? kanonischerText(u.bis), grund: kanonischerText(u.grund) } : null,
    },
    stillstand: nichtLeer(i.stillstand),
    belegschaft: {
      eigene: i.belegschaft.eigene.map((e) => ({ qualifikation: kanonischerText(e.qualifikation ?? ''), anzahl: ganz(e.anzahl), stunden: stunden(e.stunden) })),
      namen: i.belegschaft.namen ? i.belegschaft.namen.map((n) => kanonischerText(n)).filter(Boolean) : null,
      fremd: i.belegschaft.fremd.map((f) => ({ firma: kanonischerText(f.firma ?? ''), anzahl: ganz(f.anzahl), taetigkeit: nichtLeer(f.taetigkeit) })),
    },
    geraete: i.geraete.map((g) => ({
      bezeichnung: kanonischerText(g.bezeichnung ?? ''),
      artikelnummer: nichtLeer(g.artikelnummer),
      anzahl: ganz(g.anzahl),
      stunden: tagebuchZahl(g.stunden) === null ? null : stunden(g.stunden),
    })),
    leistungen: i.leistungen.map((l) => kanonischerText(l)),
    lieferungen: i.lieferungen.map((l) => ({ text: kanonischerText(l.text ?? ''), lieferant: nichtLeer(l.lieferant), lieferschein: nichtLeer(l.lieferschein) })),
    behinderungen: [...i.behinderungen]
      .sort((a, b) => a.nr - b.nr)
      .map((h) => ({
        nr: h.nr,
        kette: kanonischerText(h.kette ?? ''),
        art: h.art,
        text: kanonischerText(h.text ?? ''),
        seit: (h.seit ?? '').slice(0, 10),
        bis: h.bis ? h.bis.slice(0, 10) : null,
        auswirkung: nichtLeer(h.auswirkung),
        mehrkosten: !!h.mehrkosten,
        angezeigtAm: h.angezeigtAm ? h.angezeigtAm.slice(0, 10) : null,
      })),
    anordnungen: i.anordnungen.map((a) => ({ von: kanonischerText(a.von ?? ''), text: kanonischerText(a.text ?? '') })),
    vorkommnisse: nichtLeer(i.vorkommnisse),
    bemerkung: nichtLeer(i.bemerkung),
  }
}

// ── Pflicht vor dem Abschluss (5.2) ──────────────────────────────────────────

const DATUM = /^\d{4}-\d{2}-\d{2}$/
const minuten = (zeit: string | null): number | null => {
  const z = tagebuchUhrzeit(zeit)
  return z ? Number(z.slice(0, 2)) * 60 + Number(z.slice(3)) : null
}

/**
 * **Pflicht vor dem Abschluss** (AP-48 5.2) — Fehlerliste, je Lücke ein Schlüssel; der letzte Teil
 * nach dem Doppelpunkt ist der Übersetzungsschlüssel (`eigene:2:anzahl` → „anzahl").
 *
 * - Wetter: mindestens eine Lage;
 * - Stillstand mit Grund **oder** mindestens eine Zeile Belegschaft (eigene oder fremde) und eine Leistung;
 * - jede Zeile vollständig; die Unterbrechung mit von vor bis und Grund;
 * - jede Behinderung mit Art, Beschreibung, seit; `bis` nicht vor `seit`; jede Anordnung mit von und Text.
 */
export function tagebuchPruefe(roh: TagebuchInhalt | Record<string, unknown> | null): string[] {
  const i = tagebuchInhalt(roh)
  const f: string[] = []
  if (!i.wetter.lage.length) f.push('wetter')
  const stillstand = !!nichtLeer(i.stillstand)
  if (!stillstand) {
    if (!i.belegschaft.eigene.length && !i.belegschaft.fremd.length) f.push('belegschaft')
    if (!i.leistungen.some((l) => !!nichtLeer(l))) f.push('leistungen')
  }
  i.belegschaft.eigene.forEach((e, n) => {
    if (!nichtLeer(e.qualifikation)) f.push(`eigene:${n}:qualifikation`)
    if (!((tagebuchZahl(e.anzahl) ?? 0) > 0)) f.push(`eigene:${n}:anzahl`)
    const h = tagebuchZahl(e.stunden)
    if (h === null || h < 0) f.push(`eigene:${n}:stunden`)
  })
  i.belegschaft.fremd.forEach((x, n) => {
    if (!nichtLeer(x.firma)) f.push(`fremd:${n}:firma`)
    if (!((tagebuchZahl(x.anzahl) ?? 0) > 0)) f.push(`fremd:${n}:anzahl`)
  })
  i.geraete.forEach((g, n) => {
    if (!nichtLeer(g.bezeichnung)) f.push(`geraet:${n}:bezeichnung`)
    if (!((tagebuchZahl(g.anzahl) ?? 0) > 0)) f.push(`geraet:${n}:anzahl`)
  })
  i.leistungen.forEach((l, n) => {
    if (!nichtLeer(l)) f.push(`leistung:${n}:leistung`)
  })
  i.lieferungen.forEach((l, n) => {
    if (!nichtLeer(l.text)) f.push(`lieferung:${n}:lieferung`)
  })
  const u = i.wetter.unterbrechung
  if (u) {
    const von = minuten(u.von)
    const bis = minuten(u.bis)
    if (von === null || bis === null || bis <= von || !nichtLeer(u.grund)) f.push('unterbrechung')
  }
  for (const h of i.behinderungen) {
    if (!(TAGEBUCH_BEHINDERUNG_ARTEN as readonly string[]).includes(h.art)) f.push(`behinderung:${h.nr}:art`)
    if (!nichtLeer(h.text)) f.push(`behinderung:${h.nr}:text`)
    if (!h.seit || !DATUM.test(h.seit.slice(0, 10))) f.push(`behinderung:${h.nr}:seit`)
    else if (h.bis && h.bis.slice(0, 10) < h.seit.slice(0, 10)) f.push(`behinderung:${h.nr}:bis`)
  }
  i.anordnungen.forEach((a, n) => {
    if (!nichtLeer(a.von)) f.push(`anordnung:${n}:von`)
    if (!nichtLeer(a.text)) f.push(`anordnung:${n}:text`)
  })
  return f
}

// ── Einstellungen der Art (7., RN-2) ─────────────────────────────────────────

/** Einstellungen der Art in `SiteDocumentSetting.extra`. */
export interface TagebuchExtra {
  /** Namen der eigenen Belegschaft im Bericht zeigen (TB-5) — Vorgabe aus. */
  namenZeigen: boolean
}

export const TAGEBUCH_EXTRA_VORGABE: TagebuchExtra = { namenZeigen: false }

export function tagebuchExtra(extra: unknown): TagebuchExtra {
  const e = (extra ?? {}) as Partial<TagebuchExtra>
  return { namenZeigen: e.namenZeigen === true }
}

/**
 * Prüft die Einstellungen der Art, bevor sie gespeichert werden — Fehlerliste, leer = in Ordnung.
 *
 * **Noch nicht eingehängt:** Den Haken dafür am Rahmen (`pruefeExtra` in `SiteDocKindDefinition`,
 * gerufen von `SiteDocsSettingsService`) baut AP-47 als RN-2; diese Funktion ist der Beitrag des
 * Bautagebuchs dazu.
 */
export function tagebuchPruefeExtra(extra: unknown): string[] {
  if (extra == null) return []
  if (typeof extra !== 'object' || Array.isArray(extra)) return ['extra']
  const fehler: string[] = []
  for (const [k, v] of Object.entries(extra as Record<string, unknown>)) {
    if (k === 'namenZeigen') {
      if (typeof v !== 'boolean') fehler.push('extra.namenZeigen')
    } else {
      fehler.push(`extra.${k}`)
    }
  }
  return fehler
}

// ── Vorbelegung (4.2, 4.3, 4.4) ──────────────────────────────────────────────

/** Eine Buchung, wie die Vorbelegung sie zusammenfasst — Netto-Minuten (`duration` ist netto). */
export interface TagebuchBuchung {
  userId: string
  userName: string
  minutes: number
  qualification: string | null
}

const stundenGerundet = (minuten: number) => Math.round((minuten / 60) * 100) / 100

/**
 * AP-48 4.2 — **Belegschaft aus den Buchungen**: je Qualifikation die Anzahl **verschiedener**
 * Mitarbeiter und die Summe der Stunden (netto, auf zwei Stellen). Leere Qualifikation → „ohne Angabe",
 * am Ende. Namen nur, wenn der Mandant sie zeigt (TB-5) — sonst `null`.
 */
export function tagebuchBelegschaftAusBuchungen(buchungen: readonly TagebuchBuchung[], namenZeigen: boolean): { eigene: TagebuchEigene[]; namen: string[] | null } {
  const je = new Map<string, { leute: Set<string>; minuten: number }>()
  for (const b of buchungen) {
    const q = b.qualification?.trim() || TAGEBUCH_OHNE_QUALIFIKATION
    const w = je.get(q) ?? { leute: new Set<string>(), minuten: 0 }
    w.leute.add(b.userId)
    w.minuten += Math.max(0, b.minutes || 0)
    je.set(q, w)
  }
  const eigene = [...je.entries()]
    .sort(([a], [b]) => (a === TAGEBUCH_OHNE_QUALIFIKATION ? 1 : b === TAGEBUCH_OHNE_QUALIFIKATION ? -1 : a.localeCompare(b, 'de')))
    .map(([qualifikation, w]) => ({ qualifikation, anzahl: w.leute.size, stunden: stundenGerundet(w.minuten), vorbelegt: true }))
  if (!namenZeigen) return { eigene, namen: null }
  const namen = new Map<string, string>()
  for (const b of buchungen) if (!namen.has(b.userId)) namen.set(b.userId, b.userName)
  return { eigene, namen: [...namen.values()].sort((a, b) => a.localeCompare(b, 'de')) }
}

/** Summe der Personen und Stunden — eigene und fremde Personen, Stunden nur der eigenen. */
export function tagebuchSummen(roh: TagebuchInhalt | Record<string, unknown> | null): { personen: number; eigene: number; fremde: number; stunden: number } {
  const i = tagebuchInhalt(roh)
  const eigene = i.belegschaft.eigene.reduce((s, e) => s + Math.round(tagebuchZahl(e.anzahl) ?? 0), 0)
  const fremde = i.belegschaft.fremd.reduce((s, e) => s + Math.round(tagebuchZahl(e.anzahl) ?? 0), 0)
  const h = i.belegschaft.eigene.reduce((s, e) => s + (tagebuchZahl(e.stunden) ?? 0), 0)
  return { personen: eigene + fremde, eigene, fremde, stunden: Math.round(h * 100) / 100 }
}

/** Was aus dem letzten freigegebenen Bericht desselben Projekts übernommen wird (4.3). */
export interface TagebuchLetzterBericht {
  documentId: string
  number: string | null
  documentDate: string
  geraete: TagebuchGeraet[]
  fremd: TagebuchFremd[]
  /** offene Behinderungen (`bis` leer) */
  behinderungen: TagebuchBehinderung[]
}

/**
 * `GET /site-docs/diary/prefill` — die Vorbelegung eines neuen Berichts. `belegschaft` ist `null`, wenn
 * es keine Projektzeit gibt (ohne `TIME_TRACKING.PROJECT_TIME` oder bei einem frei eingetragenen Projekt).
 */
export interface TagebuchVorbelegung {
  belegschaft: { eigene: TagebuchEigene[]; namen: string[] | null } | null
  /** Zahl der Buchungen dahinter — 0 heißt „niemand hat gebucht". */
  buchungen: number
  /** aus den Wareneingängen des Tages (RN-7) — leer ohne Projektverwaltung */
  lieferungen: TagebuchLieferung[]
  letzter: TagebuchLetzterBericht | null
}

/** `GET /site-docs/diary/device-cache` — je Projekt der Auswahl, was der letzte Bericht mitgibt (4.3). */
export interface TagebuchGeraeteVorrat {
  /** Schlüssel: `projectId` bzw. `projectRefId` */
  projekte: { projectId: string | null; projectRefId: string | null; letzter: TagebuchLetzterBericht }[]
  /** Geräte aus dem Artikelstamm (`EQUIPMENT`) — nur mit `ARTICLES` */
  articles: { id: string; number: string; name: string }[]
  namenZeigen: boolean
  fetchedAt: string
}

/**
 * Was ein neuer Bericht aus dem letzten übernimmt (4.3, 4.4): Geräte und Fremdfirmen markiert, offene
 * Behinderungen mit derselben Kette, neu durchnummeriert und mit „dauert an?" (`fortgeschrieben`).
 * Leistungen, Wetter und Stunden gehören dem Tag und werden nie übernommen.
 */
export function tagebuchAusLetztem(inhalt: TagebuchInhalt, letzter: TagebuchLetzterBericht | null): TagebuchInhalt {
  if (!letzter) return inhalt
  const ketten = new Set(inhalt.behinderungen.map((b) => b.kette))
  let nr = inhalt.behinderungen.reduce((m, b) => Math.max(m, b.nr), 0)
  const fortgeschrieben = letzter.behinderungen
    .filter((b) => !b.bis && !ketten.has(b.kette))
    .sort((a, b) => a.nr - b.nr)
    .map((b) => ({ ...b, nr: ++nr, bis: null, fortgeschrieben: true, artVorschlag: false }))
  return {
    ...inhalt,
    geraete: inhalt.geraete.length ? inhalt.geraete : letzter.geraete.map((g) => ({ ...g, vorbelegt: true })),
    belegschaft: { ...inhalt.belegschaft, fremd: inhalt.belegschaft.fremd.length ? inhalt.belegschaft.fremd : letzter.fremd.map((f) => ({ ...f, vorbelegt: true })) },
    behinderungen: [...inhalt.behinderungen, ...fortgeschrieben],
  }
}

/** Die nächste freie Nr. einer Behinderung im Bericht. */
export function tagebuchNaechsteNr(inhalt: Pick<TagebuchInhalt, 'behinderungen'>): number {
  return inhalt.behinderungen.reduce((m, b) => Math.max(m, b.nr), 0) + 1
}

// ── Kalender, Behinderungsliste, Akte (7.) ───────────────────────────────────

/** Werte des Berichts für die eigenen Spalten der Akte (7.). */
export interface TagebuchListenWerte {
  persons: number
  hours: number
  hindrance: boolean
  standstill: boolean
  countersigned: boolean
  objection: boolean
}

/** Ein Tag im Monatskalender des Bautagebuchs (7.). */
export interface TagebuchKalenderTag {
  date: string
  reports: {
    id: string
    number: string | null
    status: string
    title: string
    createdByName: string
    countersigned: boolean
    objection: boolean
    standstill: boolean
    persons: number
    hours: number
  }[]
  /** Buchungen auf dem Projekt an diesem Tag (nur mit `TIME_TRACKING.PROJECT_TIME`) */
  bookings: number
  /** Buchungen ohne aktiven Bericht — ein Hinweis, keine Pflicht (TB-12) */
  missing: boolean
}

export interface TagebuchKalender {
  month: string
  /** Gibt es Projektzeit beim Mandanten? Sonst zeigt der Kalender kein „Bericht fehlt". */
  bookingsAvailable: boolean
  days: TagebuchKalenderTag[]
}

/** Eine Kette der Behinderungsliste (4.4, 7.) — die Grundlage einer Mehrkostenforderung. */
export interface TagebuchBehinderungsKette {
  chainId: string
  projectLabel: string
  projectId: string | null
  projectRefId: string | null
  art: TagebuchBehinderungArt
  text: string
  firstDay: string
  lastDay: string
  /** Arbeitstage Montag bis Freitag zwischen dem ersten und dem letzten Tag, beide eingeschlossen */
  workDays: number
  ongoing: boolean
  costExpected: boolean
  notifiedOn: string | null
  effect: string | null
  reports: string[]
  updatedAt: string
}

/** Arbeitstage (Mo–Fr) zwischen zwei Kalendertagen, beide eingeschlossen — Feiertage zählen mit. */
export function tagebuchArbeitstage(von: string, bis: string): number {
  const a = Date.UTC(Number(von.slice(0, 4)), Number(von.slice(5, 7)) - 1, Number(von.slice(8, 10)))
  const b = Date.UTC(Number(bis.slice(0, 4)), Number(bis.slice(5, 7)) - 1, Number(bis.slice(8, 10)))
  if (!(b >= a)) return 0
  let tage = 0
  for (let t = a; t <= b; t += 86_400_000) {
    const w = new Date(t).getUTCDay()
    if (w !== 0 && w !== 6) tage++
  }
  return tage
}

export const TAGEBUCH_BEHINDERUNG_SPALTEN: readonly { key: keyof TagebuchBehinderungsKette | 'reportsText'; label: string }[] = [
  { key: 'projectLabel', label: 'Projekt' },
  { key: 'art', label: 'Art' },
  { key: 'text', label: 'Beschreibung' },
  { key: 'firstDay', label: 'Erster Tag' },
  { key: 'lastDay', label: 'Letzter Tag' },
  { key: 'workDays', label: 'Arbeitstage' },
  { key: 'ongoing', label: 'Dauert an' },
  { key: 'costExpected', label: 'Mehrkosten zu erwarten' },
  { key: 'notifiedOn', label: 'Angezeigt am' },
  { key: 'effect', label: 'Auswirkung' },
  { key: 'reportsText', label: 'Berichte' },
]

export const TAGEBUCH_BEHINDERUNG_TEXTE: Record<TagebuchBehinderungArt, string> = {
  WITTERUNG: 'Witterung',
  VORLEISTUNG: 'fehlende Vorleistung',
  PLAENE: 'fehlende Pläne/Freigaben',
  ANORDNUNG: 'Anordnung des Auftraggebers',
  ZUGANG: 'Zugang/Logistik',
  SONSTIGES: 'Sonstiges',
}

export const TAGEBUCH_WETTER_TEXTE: Record<TagebuchWetterLage, string> = {
  SONNIG: 'sonnig',
  HEITER: 'heiter',
  BEWOELKT: 'bewölkt',
  NEBEL: 'Nebel',
  REGEN: 'Regen',
  SCHNEE: 'Schnee',
  GEWITTER: 'Gewitter',
  FROST: 'Frost',
  HITZE: 'Hitze',
  STURM: 'Sturm',
}

export const TAGEBUCH_NIEDERSCHLAG_TEXTE: Record<TagebuchNiederschlag, string> = { KEINER: 'kein Niederschlag', LEICHT: 'leichter Niederschlag', STARK: 'starker Niederschlag' }
export const TAGEBUCH_WIND_TEXTE: Record<TagebuchWind, string> = { SCHWACH: 'schwacher Wind', MAESSIG: 'mäßiger Wind', STARK: 'starker Wind', STURM: 'Sturm' }

/** Wetter in einer Zeile — PDF, Vorzeige, Assistent: „Regen, bewölkt · 8 °C / 12 °C · leichter Niederschlag". */
export function tagebuchWetterZeile(w: Pick<TagebuchWetter, 'lage' | 'tempMorgen' | 'tempMittag' | 'niederschlag' | 'wind'>): string {
  const teile: string[] = []
  const lage = TAGEBUCH_WETTER_LAGEN.filter((l) => w.lage.includes(l)).map((l) => TAGEBUCH_WETTER_TEXTE[l])
  if (lage.length) teile.push(lage.join(', '))
  const m = tagebuchZahl(w.tempMorgen)
  const mi = tagebuchZahl(w.tempMittag)
  if (m !== null || mi !== null) teile.push(`${m !== null ? `${Math.round(m)} °C` : '–'} / ${mi !== null ? `${Math.round(mi)} °C` : '–'}`)
  if (w.niederschlag) teile.push(TAGEBUCH_NIEDERSCHLAG_TEXTE[w.niederschlag])
  if (w.wind) teile.push(TAGEBUCH_WIND_TEXTE[w.wind])
  return teile.join(' · ')
}

/** Stunden für Anzeige und PDF: `6,5` — höchstens zwei Nachkommastellen. */
export function tagebuchStundenText(h: number): string {
  return String(Math.round(h * 100) / 100).replace('.', ',')
}

// ── Diktat (13.) ─────────────────────────────────────────────────────────────

/** Ein Eintrag des Diktat-Vorschlags — die Abschnitte, die die KI füllen darf (TB-15). */
export type TagebuchDiktatEintrag =
  | { abschnitt: 'leistung'; text: string }
  | { abschnitt: 'lieferung'; text: string; lieferant: string | null; lieferschein: string | null }
  | { abschnitt: 'geraet'; bezeichnung: string; anzahl: number; stunden: number | null; artikelId: string | null; artikelnummer: string | null }
  | { abschnitt: 'fremd'; firma: string; anzahl: number; taetigkeit: string | null }
  | { abschnitt: 'behinderung'; art: TagebuchBehinderungArt; text: string; seit: string }
  | { abschnitt: 'anordnung'; von: string; text: string }
  | { abschnitt: 'wetter'; lage: TagebuchWetterLage }
  | { abschnitt: 'vorkommnis'; text: string }

/**
 * Einen übernommenen Diktat-Eintrag in den Inhalt einfügen — hinter das Vorhandene. Eigene Belegschaft,
 * Stunden, Temperatur und Gegenzeichnung fasst das Diktat nie an (TB-15). Die Art einer Behinderung
 * bleibt markiert, bis der Erfasser sie bestätigt.
 */
export function tagebuchMitDiktat(inhalt: TagebuchInhalt, eintraege: readonly TagebuchDiktatEintrag[], kette: () => string): TagebuchInhalt {
  const neu: TagebuchInhalt = {
    ...inhalt,
    wetter: { ...inhalt.wetter, lage: [...inhalt.wetter.lage] },
    belegschaft: { ...inhalt.belegschaft, fremd: [...inhalt.belegschaft.fremd] },
    geraete: [...inhalt.geraete],
    leistungen: [...inhalt.leistungen],
    lieferungen: [...inhalt.lieferungen],
    behinderungen: [...inhalt.behinderungen],
    anordnungen: [...inhalt.anordnungen],
  }
  for (const e of eintraege) {
    switch (e.abschnitt) {
      case 'leistung':
        neu.leistungen.push(e.text)
        break
      case 'lieferung':
        neu.lieferungen.push({ text: e.text, lieferant: e.lieferant, lieferschein: e.lieferschein })
        break
      case 'geraet':
        neu.geraete.push({ bezeichnung: e.bezeichnung, artikelnummer: e.artikelnummer, artikelId: e.artikelId, anzahl: e.anzahl, stunden: e.stunden })
        break
      case 'fremd':
        neu.belegschaft.fremd.push({ firma: e.firma, anzahl: e.anzahl, taetigkeit: e.taetigkeit })
        break
      case 'behinderung':
        neu.behinderungen.push({ nr: tagebuchNaechsteNr(neu), kette: kette(), art: e.art, text: e.text, seit: e.seit, bis: null, auswirkung: null, mehrkosten: false, angezeigtAm: null, artVorschlag: true })
        break
      case 'anordnung':
        neu.anordnungen.push({ von: e.von, text: e.text })
        break
      case 'wetter':
        if (!neu.wetter.lage.includes(e.lage)) neu.wetter.lage.push(e.lage)
        break
      case 'vorkommnis':
        neu.vorkommnisse = [neu.vorkommnisse, e.text].filter((t) => t && t.trim()).join('\n')
        break
    }
  }
  return neu
}
